







































import gql from 'graphql-tag';
import Vue from 'vue';
import { mapActions, mapGetters } from 'vuex';
import { BAlert } from 'bootstrap-vue';
import { get, clone, find, isEmpty } from 'lodash';
import ZenMultiselect from '@reciprocity/multiselect';
import { DASHBOARD_LIST_QUERY } from '@/pages/dashboards/DashboardListPage.vue';
import GenericCard from '@/components/cards/GenericCard.vue';
import { ModalSize } from '@/components/zen_modal/types';
import { Alert } from '@/components/global_alerts/types';

const DASHBOARD_OWNERS_QUERY = gql`
  query DashboardOwners($id: ID!) {
    node(id: $id) {
      ... on Dashboard {
        id
        owners {
          value {
            edges {
              node {
                id
                title: email {
                  value
                }
              }
            }
          }
        }
      }
    }
  }
`;

const DASHBOARD_GROUP_OWNERS_QUERY = gql`
  query DashboardOwners($id: ID!) {
    node(id: $id) {
      ... on Dashboard {
        id
        owners: ownersGroup {
          value {
            edges {
              node {
                ... on Group {
                  id
                  title {
                    value
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`;

const UPDATE_DASHBOARD_OWNERS_MUTATION = gql`
  mutation UpdateDashboardOwners($id: ID!, $input: UpdateDashboardInput!) {
    updateDashboard(id: $id, input: $input) {
      result {
        id
        owners {
          value {
            edges {
              node {
                id
                title: email {
                  value
                }
              }
            }
          }
        }
      }
    }
  }
`;

const UPDATE_DASHBOARD_GROUP_OWNERS_MUTATION = gql`
  mutation UpdateDashboardOwners($id: ID!, $input: UpdateDashboardInput!) {
    updateDashboard(id: $id, input: $input) {
      result {
        id
        ownersGroup {
          value {
            edges {
              node {
                ... on Group {
                  id
                  title {
                    value
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`;

export default Vue.extend({
  name: 'ShareDashboardCard',
  components: {
    GenericCard,
    BAlert,
    ZenMultiselect,
  },
  props: {
    dashboardTitle: {
      type: String,
      required: false,
      default: '',
    },
    dashboardId: {
      type: String,
      required: true,
    },
  },
  apollo: {
    groups: {
      query() {
        return this.featureFlags.ac_groups
          ? gql`
              query GetGroups {
                search(type: Group) {
                  edges {
                    node {
                      ... on Group {
                        id
                        title {
                          value
                        }
                        people {
                          value {
                            edges {
                              node {
                                dbId
                              }
                            }
                          }
                        }
                      }
                    }
                  }
                }
              }
            `
          : gql`
              query GetPeople {
                search(type: Person) {
                  edges {
                    node {
                      ... on Person {
                        id
                        dbId
                        title: email {
                          value
                        }
                      }
                    }
                  }
                }
              }
            `;
      },
      update(result) {
        const groups = get(result, 'search.edges', []).map(r => r.node, []);
        return groups.map(group => {
          let peopleIds = [group.dbId];

          if (group.people) {
            peopleIds = get(group, 'people.value.edges', []).map(edge => get(edge, 'node.dbId'));
          }

          return {
            id: group.id,
            title: group.title.value,
            peopleIds,
          };
        });
      },
    },
    owners: {
      query() {
        return this.featureFlags.ac_groups ? DASHBOARD_GROUP_OWNERS_QUERY : DASHBOARD_OWNERS_QUERY;
      },
      variables() {
        return {
          id: this.dashboardId,
        };
      },
      update(result) {
        const nodes = get(result, 'node.owners.value.edges').map(edge => edge.node);

        const owners = nodes.map(owner => ({
          value: {
            id: owner.id,
            title: owner.title.value,
          },
        }));

        if (!this.previousOwners) {
          this.previousOwners = clone(owners);
        }

        return owners;
      },
    },
  },
  data() {
    return {
      groups: null,
      owners: [],
      previousOwners: null,
      alerts: <Alert[]>[],
      saving: false,
    };
  },
  computed: {
    ...mapGetters(['featureFlags']),
    filteredGroups() {
      if (!this.groups) return [];
      // filter out groups that are already selected
      return this.groups.filter(group => !this.owners.map(owner => owner.value.id).includes(group.id));
    },
  },
  created() {
    this.changeModalSize(ModalSize.Small);
  },
  methods: {
    ...mapActions({
      changeModalSize: 'modals/changeSize',
      cancelModal: 'modals/cancel',
      confirmModal: 'modals/confirm',
      showAlert: 'alerts/show',
    }),
    addOwner() {
      this.owners.push({
        value: {},
      });
    },
    removeOwner(index: number) {
      this.owners.splice(index, 1);
    },
    async save() {
      if (!this.isValid()) return;

      try {
        this.saving = true;

        if (this.featureFlags.ac_groups) {
          await this.$apollo.mutate({
            mutation: UPDATE_DASHBOARD_GROUP_OWNERS_MUTATION,
            variables: {
              id: this.dashboardId,
              input: {
                ownersGroup: this.owners.map(o => o.value.id),
              },
            },
            refetchQueries: [
              {
                query: DASHBOARD_GROUP_OWNERS_QUERY,
                variables: {
                  id: this.dashboardId,
                },
              },
              {
                query: DASHBOARD_LIST_QUERY,
              },
            ],
          });
        } else {
          await this.$apollo.mutate({
            mutation: UPDATE_DASHBOARD_OWNERS_MUTATION,
            variables: {
              id: this.dashboardId,
              input: {
                owners: this.owners.map(o => o.value.id),
              },
            },
            refetchQueries: [
              {
                query: DASHBOARD_OWNERS_QUERY,
                variables: {
                  id: this.dashboardId,
                },
              },
              {
                query: DASHBOARD_LIST_QUERY,
              },
            ],
          });
        }

        this.notifyNewOwners();

        this.showAlert({
          type: 'success',
          message: `Sharing for '${this.dashboardTitle}' updated successfully`,
        });

        this.confirmModal();
      } catch {
        this.showAlert({
          type: 'error',
          message: `Failed updating sharing for '${this.dashboardTitle}'.`,
        });
      } finally {
        this.saving = false;
      }
    },
    isValid() {
      this.alerts = [];
      if (!this.owners.length) {
        this.alerts.push(this.getError(`${this.dashboardTitle} needs to have at least one owner.`));
      } else {
        if (this.owners.filter(o => isEmpty(o.value)).length) {
          this.alerts.push(this.getError(`Owner selection is required.`));
        }
      }
      return !this.alerts.length;
    },
    getError(message: string) {
      return {
        type: 'error',
        message,
      };
    },
    notifyNewOwners() {
      const { owners, groups, previousOwners, dashboardId } = this;

      const newOwners = owners.filter(owner => {
        const ownerId = get(owner, 'value.id');

        const previous = find(previousOwners, previousOwner => {
          return get(previousOwner, 'value.id') === ownerId;
        });

        return !previous;
      });

      const peopleIds = newOwners.reduce((ids, owner) => {
        const group = find(groups, group => get(group, 'id') === get(owner, 'value.id'));
        ids = ids.concat(get(group, 'peopleIds', []));
        return ids;
      }, []);

      peopleIds.forEach(peopleId => {
        this.$apollo.mutate({
          mutation: gql`
            mutation ShareDashboard($dashboardId: Int, $peopleId: Int) {
              shareDashboard(dashboardId: $dashboardId, peopleId: $peopleId) {
                id
              }
            }
          `,
          variables: {
            // TODO: Remove spliting
            dashboardId: parseInt(dashboardId.split(':')[1]),
            peopleId,
          },
        });
      });
    },
  },
});
